import React, {
  useState,
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { useTheme } from '@material-ui/core';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment';

import { convertObjToArr } from '../../../../utils/index';
import Actions from '../../../../store/ducks/appointments';

const ScheduleContext = createContext();

const ScheduleProvider = ({ children }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    columns: { list: columnsSchedule },
    filters: appliedFilters,
  } = useSelector(state => state.appointments.schedule);

  const [scheduleAppointments, setScheduleAppointments] = useState([]);
  const [headerSwiper, setHeaderSwiper] = useState(null);
  const [appointmentsSwiper, setAppointmentsSwiper] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState(0);
  const [initialRequestAppointments, setInitialRequestAppointments] = useState(
    false,
  );
  const [isInitialSlide, setIsInitialSlide] = useState(true);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [filterProfessional, setFilterProfessional] = useState(null);
  const [filterDateRange, setFilterDateRange] = useState([]);

  const minColumnWidth = useMemo(() => theme.spacing(22), [theme]);
  const preLoadCount = useMemo(() => 7, []);
  const initialDateRange = useMemo(() => {
    const startDate = moment().utc().toISOString();
    const endDate = moment(startDate)
      .add(visibleColumns + preLoadCount, 'd')
      .toISOString();
    return [startDate, endDate];
  }, [preLoadCount, visibleColumns]);

  // Set initial date range
  useEffect(() => {
    if (!visibleColumns || initialRequestAppointments) return;
    setFilterDateRange(initialDateRange);
    setInitialRequestAppointments(true);
  }, [visibleColumns, initialRequestAppointments, initialDateRange]);

  // Update state with array of colums
  useEffect(() => {
    setScheduleAppointments(convertObjToArr(columnsSchedule));
  }, [columnsSchedule]);

  const checkToAddColumns = useCallback(
    (startDate, endDate) => {
      const daysLength = moment(endDate).diff(moment(startDate), 'days');
      const columnsApplied = convertObjToArr(columnsSchedule);
      const columnsAppliedLength = columnsApplied.length;

      // Meu add ta adicionando todo mundo de novo, ao inves de adicionar apenas as novas colunas...

      if (columnsAppliedLength) {
        const startDateApplied = columnsApplied[0]?.date;
        const endDateApplied = columnsApplied[columnsAppliedLength - 1]?.date;
        const hasNewBefore = moment(startDate).isBefore(
          moment(startDateApplied),
        );
        const hasNewAfter = moment(endDate)
          .subtract(1, 'd')
          .isAfter(moment(endDateApplied));

        if (!hasNewBefore && !hasNewAfter) return;

        if (hasNewBefore) {
          const endDateBefore = moment(startDateApplied)
            .subtract(1, 'd')
            .toISOString();
          const lengthBefore = moment(endDateBefore).diff(
            moment(startDate),
            'days',
          );
          dispatch(Actions.addScheduleColumns(startDate, lengthBefore));
        }

        if (hasNewAfter) {
          const startDateAfter = moment(endDateApplied)
            .add(1, 'd')
            .toISOString();
          const lengthAfter = moment(endDate).diff(
            moment(startDateAfter),
            'days',
          );
          dispatch(Actions.addScheduleColumns(startDateAfter, lengthAfter));
        }
      } else {
        dispatch(Actions.addScheduleColumns(startDate, daysLength));
      }
    },
    [dispatch, columnsSchedule],
  );

  // const checkToRemoveColumns = useCallback(
  //   (startDate, endDate) => {
  //     const columnsApplied = convertObjToArr(columnsSchedule);
  //     const columnsAppliedLength = columnsApplied.length;
  //     if (columnsAppliedLength) {
  //       const startDateApplied = columnsApplied[0]?.date;
  //       const endDateApplied = columnsApplied[columnsAppliedLength - 1]?.date;
  //       const hasAppliedBefore = moment(startDateApplied).isBefore(
  //         moment(startDate),
  //       );
  //       const hasAppliedAfter = moment(endDateApplied).isAfter(moment(endDate));

  //       // if (!hasAppliedBefore && !hasAppliedAfter) return;

  //       // const columnsToRemoveBefore = [
  //       //   startDateApplied,
  //       //   moment(startDate).subtract(1, 'd').toISOString(),
  //       // ];
  //       // const columnsToRemoveAfter = [
  //       //   moment(endDate).add(1, 'd').toISOString(),
  //       //   endDateApplied,
  //       // ];
  //       // TODO:
  //       // dispatch(Actions.removeScheduleColumns(columnsToRemoveBefore, columnsToRemoveAfter));
  //     }
  //   },
  //   [columnsSchedule],
  // );

  // On change date range
  useEffect(() => {
    if (!filterDateRange.length) return;

    const [startDate, endDate] = filterDateRange;
    checkToAddColumns(startDate, endDate);
  }, [filterDateRange, checkToAddColumns]);

  const resetSchedule = useCallback(() => {
    dispatch(Actions.resetScheduleColumns());
    setFilterDateRange(initialDateRange);
  }, [dispatch, initialDateRange]);

  const refreshSchedule = useCallback(() => {
    if (!filterDateRange.length) return;
    dispatch(Actions.resetScheduleColumns());

    const [startDate, endDate] = filterDateRange;
    checkToAddColumns(startDate, endDate);
  }, [checkToAddColumns, dispatch, filterDateRange]);

  // Reset columns on change filter
  useEffect(() => {
    if (filterProfessional !== appliedFilters.professional) {
      resetSchedule();
    }
  }, [resetSchedule, filterProfessional, appliedFilters.professional]);

  useEffect(() => {
    return () => {
      resetSchedule();
    };
  }, [resetSchedule]);

  // Check data on update columns
  useEffect(() => {
    const columnsApplied = convertObjToArr(columnsSchedule);
    const columnsUnloaded = columnsApplied.filter(column => !column.loaded);
    if (columnsUnloaded.length) {
      const columnsUnloadedRanges = columnsUnloaded.reduce(
        (accumulator, currentCol) => {
          const currentDate = currentCol.date;
          const accumulatorLength = accumulator.length;
          if (accumulatorLength) {
            const lastRange = accumulator.pop();
            const lastRangeIsComplete = lastRange.length > 1;
            const isSequentialDate = moment(
              lastRange[lastRange.length - 1],
            ).isSame(moment(currentDate).subtract(1, 'd'), 'day');
            if (isSequentialDate) {
              return [...accumulator, [lastRange[0], currentDate]];
            }
            if (lastRangeIsComplete) {
              return [...accumulator, lastRange, [currentDate]];
            }
            return [
              ...accumulator,
              [lastRange[0], lastRange[0]],
              [currentDate],
            ];
          }
          return [[currentDate]];
        },
        [],
      );
      columnsUnloadedRanges.map(([startDate, endDate]) =>
        dispatch(
          Actions.requestScheduleColumns(
            startDate,
            endDate,
            filterProfessional,
          ),
        ),
      );
    }
  }, [dispatch, filterProfessional, columnsSchedule]);

  // Updates on slide change
  const slideChange = useCallback(() => {
    const totalSlides = headerSwiper.slides.length;
    const activeSlide = headerSwiper.activeIndex;
    setIsInitialSlide(headerSwiper.isBeginning);
    setActiveSlideIndex(activeSlide);
    const columnsAhead = totalSlides - (visibleColumns + activeSlide);
    if (columnsAhead <= 3) {
      setFilterDateRange(([oldStartDate, oldEndDate]) => [
        oldStartDate,
        moment(oldEndDate).add(preLoadCount, 'd').toISOString(),
      ]);
    }
  }, [headerSwiper, preLoadCount, visibleColumns]);

  // Callback on slide change
  useEffect(() => {
    if (headerSwiper !== null && !headerSwiper?.destroyed) {
      headerSwiper.off('slideChange');
      headerSwiper.on('slideChange', slideChange);
    }
  }, [headerSwiper, slideChange]);

  // Link Swipers
  useEffect(() => {
    if (
      appointmentsSwiper !== null &&
      appointmentsSwiper.controller &&
      headerSwiper !== null &&
      headerSwiper.controller
    ) {
      appointmentsSwiper.controller.control = headerSwiper;
      headerSwiper.controller.control = appointmentsSwiper;
    }
  }, [appointmentsSwiper, headerSwiper]);

  // Update swipers
  useEffect(() => {
    if (
      appointmentsSwiper !== null &&
      headerSwiper !== null &&
      !appointmentsSwiper?.destroyed &&
      !headerSwiper?.destroyed
    ) {
      appointmentsSwiper.params.slidesPerView = visibleColumns;
      headerSwiper.params.slidesPerView = visibleColumns;
      appointmentsSwiper.update();
      headerSwiper.update();
    }
  }, [appointmentsSwiper, headerSwiper, visibleColumns]);

  return (
    <ScheduleContext.Provider
      value={{
        isInitialSlide,
        minColumnWidth,
        visibleColumns,
        setVisibleColumns,
        scheduleAppointments,
        headerSwiper,
        setHeaderSwiper,
        appointmentsSwiper,
        setAppointmentsSwiper,
        activeSlideIndex,
        filterProfessional,
        setFilterProfessional,
        refreshSchedule,
      }}
    >
      {children}
    </ScheduleContext.Provider>
  );
};

function useSchedule() {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within an ScheduleProvider');
  }
  return context;
}

export { ScheduleProvider, useSchedule };
